import type { FC } from 'react'

import { AssetFallbackIcon, Card, Icon, LoaderCircle, StyledText, Tooltip } from '@skytech/ui'
import { Badge } from '@skytech/ui/Badge'
import { ImageWithIconFallback } from '@skytech/ui/Image'
import { useQuery } from '@tanstack/react-query'
import InfoVariantCircleOutlineIcon from 'mdi-react/InfoVariantCircleOutlineIcon'
import { useParams } from 'react-router-dom'
import { twJoin } from 'tailwind-merge'

import queries from '@/api/queries'
import { FormattedNumberWithCurrency } from '@/components/FormattedNumberWithCurrency'
import { useTranslation } from '@/components/i18n/hooks'
import { Translation } from '@/components/i18n/Translation'
import { LoadingError } from '@/components/LoadingError'
import { LocalDate } from '@/components/LocalDate'
import Page from '@/components/page/Page'
import { ecomPolicyResources } from '@/resources/ecom-policy'

import { getBadgeTypeFromStatus } from './helpers'

export const OrderDetails: FC = () => {
  const translate = useTranslation()
  const params = useParams()
  const orderId = Number(params.orderId)

  if (isNaN(orderId)) throw Error(translate('Order ID is not a number!'))

  const { data, isError } = useQuery(queries.order(orderId), () =>
    ecomPolicyResources.getOrder(orderId)
  )

  if (isError) return <LoadingError />
  if (!data) return <LoaderCircle />

  const {
    id,
    vendorSalesOrderNo,
    policyName,
    orderDate,
    status,
    deliveryDate,
    downPayments,
    estimatedDelivery,
    coverAmount,
    remainderAmount,
    vendorName,
    address,
    zip,
    city,
    deliveryAddressIsPrivate,
    productList,
    dealerEmails,
    dealerPhoneNumbers,
    dealerName,
    currency,
  } = data

  const dealerPhoneNumber = dealerPhoneNumbers ? dealerPhoneNumbers.split(',')[0] : ''
  const dealerEmail = dealerEmails ? dealerEmails.split(',')[0] : ''

  return (
    <Page pageWrapperClassName="max-w-3xl xl:pr-4">
      <h2 className="text-2xl">
        <Translation id="Ordre #{orderId}" orderId={vendorSalesOrderNo || id} />
      </h2>
      <div className="m-4 rounded-lg bg-white p-4 text-black max-md:m-2 max-[350px]:m-0">
        <Badge type={getBadgeTypeFromStatus(status)} className="lowercase">
          <Translation id={`ecom_order_status/${status}`} />
        </Badge>

        <section className="mt-4 grid grid-cols-[repeat(auto-fill,_minmax(9rem,_1fr))] rounded-xl p-3 shadow-[0_2px_8px_0_rgba(99,99,99,0.2)]">
          <div className="m-2 min-w-32 flex-1 basis-32">
            <h4 className="m-0 mb-1 text-xs font-normal leading-none">
              <Translation id="Ordredato" />
            </h4>
            <p className="!m-0 !mt-0.5 text-base leading-none">
              <LocalDate date={orderDate} />
            </p>
          </div>
          {Boolean(policyName) && (
            <div className="m-2 min-w-32 flex-1 basis-32">
              <h4 className="m-0 mb-1 text-xs font-normal leading-none">
                <Translation id="Policy" />
              </h4>
              <p className="!m-0 !mt-0.5 text-base leading-none">
                <small>{policyName}</small>
              </p>
            </div>
          )}
          {deliveryDate && (
            <div className="m-2 min-w-32 flex-1 basis-32">
              <h4 className="m-0 mb-1 text-xs font-normal leading-none">
                <Translation id="Leveringsdato" />
              </h4>
              <p className="!m-0 !mt-0.5 text-base leading-none">
                <LocalDate date={deliveryDate} />
              </p>
            </div>
          )}
          {Boolean(estimatedDelivery) && !deliveryDate && (
            <div className="m-2 min-w-32 flex-1 basis-32">
              <h4 className="m-0 mb-1 text-xs font-normal leading-none">
                <Translation id="Beregnet leveringsdato" />
              </h4>
              <p className="!m-0 !mt-0.5 text-base leading-none">
                <LocalDate date={estimatedDelivery} />
              </p>
            </div>
          )}
          {Boolean(coverAmount) && (
            <div className="m-2 min-w-32 flex-1 basis-32">
              <h4 className="m-0 mb-1 text-xs font-normal leading-none">
                <Translation id="Total kost" />
              </h4>
              <p className="!m-0 !mt-0.5 text-base leading-none">
                <FormattedNumberWithCurrency
                  amount={coverAmount + remainderAmount}
                  currency={currency}
                />
              </p>
            </div>
          )}
          {Boolean(coverAmount) && (
            <div className="m-2 min-w-32 flex-1 basis-32">
              <h4 className="m-0 mb-1 text-xs font-normal leading-none">
                <Translation id="Dekket kost" />
              </h4>
              <p className="!m-0 !mt-0.5 text-base leading-none">
                <FormattedNumberWithCurrency amount={coverAmount} currency={currency} />
              </p>
            </div>
          )}
          {Boolean(remainderAmount) && (
            <div className="m-2 min-w-32 flex-1 basis-32">
              <h4 className="m-0 mb-1 text-xs font-normal leading-none">
                <Translation id="Lønnstrekk" />
              </h4>
              <p className="!m-0 !mt-0.5 text-base leading-none">
                <FormattedNumberWithCurrency amount={remainderAmount} currency={currency} />
              </p>
            </div>
          )}
          {Boolean(downPayments) && (
            <div className="m-2 min-w-32 flex-1 basis-32">
              <h4 className="m-0 mb-1 text-xs font-normal leading-none">
                <Translation id="Nedbetalinger" />
              </h4>
              <p className="!m-0 !mt-0.5 text-base leading-none">
                <FormattedNumberWithCurrency amount={downPayments} showCurrency={false} /> x{' '}
                <FormattedNumberWithCurrency
                  amount={remainderAmount / downPayments}
                  currency={currency}
                />{' '}
              </p>
            </div>
          )}
        </section>

        {Boolean(dealerName) && (
          <div className="mt-4 rounded-xl bg-[#f8f8f8] p-5 not-italic">
            <h4 className="m-0 mb-1.5 text-xs font-normal leading-none">
              <Translation id="Leverandør/ kontaktinfo" />
            </h4>
            <div className="text-base leading-5">
              {dealerName}
              {dealerEmail ? (
                <>
                  <br />
                  {dealerEmail}
                </>
              ) : null}
              {dealerPhoneNumber ? (
                <>
                  <br />
                  {dealerPhoneNumber}
                </>
              ) : null}
            </div>
          </div>
        )}

        <address className="mt-4 rounded-xl bg-[#f8f8f8] p-5 not-italic">
          <div className="relative mb-1.5 flex items-center gap-2">
            <h4 className="m-0 text-xs font-normal leading-none">
              <Translation id="Leveringsadresse" />
            </h4>

            <Tooltip id="delivery-address-info" size="large" />

            <Icon
              isWhiteLabeled={false}
              size="small"
              data-tooltip-id="delivery-address-info"
              data-tooltip-content={
                deliveryAddressIsPrivate
                  ? translate('This is a private address')
                  : translate('This is a business address')
              }
            >
              <InfoVariantCircleOutlineIcon />
            </Icon>
          </div>

          <div className="text-base leading-5">
            {address}
            <br />
            {zip}, {city}
          </div>
        </address>

        <h3 className="!mx-4 !mb-1 !mt-8 text-xl font-semibold leading-none text-black opacity-80">
          <Translation id="Produktliste" />
        </h3>
        {vendorName && (
          <StyledText
            marginLeft={16}
            marginRight={16}
            marginBottom={24}
            colorMode="gray"
            size="regular"
            block
          >
            {vendorName}
          </StyledText>
        )}

        <div className="grid gap-4 rounded-lg p-0">
          {productList.map(product => (
            <Card key={product.productName} noMargin className="flex w-full justify-between">
              <div className="flex w-full flex-col items-center sm:flex-row">
                <div
                  className={twJoin(
                    'flex flex-shrink-0 items-center justify-center overflow-hidden rounded-full bg-white p-2 transition-all duration-200 ease-in-out',
                    'h-16 w-16 max-md:h-12 max-md:w-12 max-sm:mb-3 max-sm:h-24 max-sm:w-24',
                    'shadow-[0_2px_4px_-1px_rgba(0,0,0,0.15),0_6px_8px_0_rgba(0,0,0,0.05),0_1px_10px_0_rgba(0,0,0,0.10)]'
                  )}
                >
                  <ImageWithIconFallback
                    fallbackIcon={<AssetFallbackIcon assetType={product.assetType || 'OTHER'} />}
                    src={product.articleImageUrl}
                    alt="Product"
                    className="flex max-w-full items-center justify-center"
                  />
                </div>

                <div className="w-full text-left sm:ml-4">
                  <h4 className="m-0 mt-0.5 text-lg font-normal leading-none text-black md:text-xl">
                    {product.productName}
                  </h4>
                  <StyledText colorMode="gray" size="small" block marginTop={2}>
                    <Translation id="produktnr" /> {product.articleNo}
                  </StyledText>

                  <div className="mt-1.5">
                    <StyledText size="large" marginRight={8}>
                      {product.quantity}x
                    </StyledText>
                    {product.cost > 0 ? (
                      <FormattedNumberWithCurrency amount={product.cost} />
                    ) : (
                      <span> — </span>
                    )}
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>
      </div>
    </Page>
  )
}
